// distributors.ts — which distributors can sell us a given material.
//
// The material supplier menu is scoped per category rather than offering every distributor in the
// referential. Two rules govern this file:
//
//  1. The referential wins and the category fills the gaps: a material's default distributor stays
//     the one the supplier referential carries (field-collected data). Category rules only become
//     the default where the referential is silent.
//  2. Brand beats category: a brand rule is more specific than a category rule, and a rule naming
//     explicit refs beats everything.
//
// No pricing effect: a distributor is not a price. It changes neither PV, PA nor margin.

import { CATALOGUE, engineSettings } from "../../data";

/**
 * Scope of a rule, most specific first. A rule fills exactly one scope field, and the field order
 * is the priority order, mirrored by `specificity`.
 */
export interface DistributorRule {
  distributor: string;
  /** Refs named one by one (the most precise scope). */
  refs?: readonly string[];
  /** Brands from the supplier referential. */
  brands?: readonly string[];
  /** Catalogue category prefixes. */
  categories?: readonly string[];
  /** Ref prefixes, or the rigid nature. */
  families?: readonly string[];
  /** Does this rule name the default distributor of its scope? */
  isDefault?: boolean;
}

/**
 * The distributor rules, read from the settings (`quote_engine.distributor_rules`). Scopes are written as
 * categories and brands rather than frozen ref lists (except where specific products were named), so a new
 * material is covered automatically. A rule may name `natures` instead of listing families: they expand to
 * every material group of that nature (single source: the material nature map).
 * @returns The rules, in priority order (an earlier rule wins a tie on specificity).
 */
export function distributorRules(): readonly DistributorRule[] {
  const { distributorRules: stored, materialNature } = engineSettings();
  return stored.map(({ natures, ...rule }) => {
    if (!natures) return rule;
    const expanded = Object.entries(materialNature)
      .filter(([, nature]) => natures.includes(nature))
      .map(([group]) => group);
    return { ...rule, families: [...(rule.families ?? []), ...expanded] };
  });
}

/**
 * Scope specificity: refs (3) > brand (2) > category (1) > family (0).
 * @param r The rule.
 * @returns The specificity rank, higher being more specific.
 */
function specificity(r: DistributorRule): number {
  if (r.refs) return 3;
  if (r.brands) return 2;
  if (r.categories) return 1;
  return 0;
}

/** Minimal material context, passed in rather than read so the module stays testable on its own. */
export interface MaterialContext {
  ref: string;
  category: string;
  /** Brand from the supplier referential, possibly absent. The explicit `| undefined` is required
   *  under `exactOptionalPropertyTypes`, where an absent field and an undefined one differ. */
  brand?: string | undefined;
}

/**
 * The catalogue material, or `null`. The brand comes from the referential and is injected.
 * @param ref The material reference.
 * @param brand The brand from the supplier referential, when known.
 * @returns The material context, or null when the material is unknown.
 */
export function materialContext(ref: string, brand?: string): MaterialContext | null {
  const m = CATALOGUE.find((x) => x.ref === ref);
  return m ? { ref, category: m.category ?? "", brand } : null;
}

/**
 * Does a rule cover this material?
 * @param r The rule.
 * @param m The material context.
 * @returns True when the rule covers the material.
 */
export function ruleCovers(r: DistributorRule, m: MaterialContext): boolean {
  if (r.refs) return r.refs.includes(m.ref);
  if (r.brands) return !!m.brand && r.brands.some((q) => m.brand!.toUpperCase().startsWith(q.toUpperCase()));
  if (r.categories) return r.categories.some((c) => m.category.toLowerCase().startsWith(c.toLowerCase()));
  if (r.families) return r.families.some((f) => m.ref.startsWith(f + "-"));
  return false;
}

/**
 * Menu for a material: every distributor a rule allows, sorted for French locale. The referential's
 * distributor is always included, even when no rule names it, since it proves we buy there.
 * @param m The material context, or null.
 * @param referentialDistributor The distributor carried by the supplier referential.
 * @returns The sorted distributor names.
 */
export function distributorMenu(m: MaterialContext | null, referentialDistributor?: string): string[] {
  const set = new Set<string>();
  if (m) for (const r of distributorRules()) if (ruleCovers(r, m)) set.add(r.distributor);
  const ref = (referentialDistributor ?? "").trim();
  if (ref) set.add(ref);
  return [...set].sort((a, b) => a.localeCompare(b, "fr"));
}

/**
 * Default distributor of a material: the referential first, else the most specific default rule.
 * Returns `null` when nothing applies, rather than inventing a supplier.
 * @param m The material context, or null.
 * @param referentialDistributor The distributor carried by the supplier referential.
 * @returns The default distributor, or null.
 */
export function defaultDistributor(m: MaterialContext | null, referentialDistributor?: string): string | null {
  const ref = (referentialDistributor ?? "").trim();
  if (ref) return ref;
  if (!m) return null;
  let winner: DistributorRule | null = null;
  for (const r of distributorRules()) {
    if (!r.isDefault || !ruleCovers(r, m)) continue;
    if (!winner || specificity(r) > specificity(winner)) winner = r;
  }
  return winner?.distributor ?? null;
}
